import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {CommonModule} from '@angular/common';
import {compare} from 'fast-json-patch';

import {MatCardModule} from '@angular/material/card';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonModule} from '@angular/material/button';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';

import {LlmSettingsService} from '../../services/llm-settings.service';
import {LlmSettingsDTO} from '../../models/llm-settings.dto';
import {LlmModelDTO} from '../../models/llm-model.dto';
import {LlmToneDTO} from '../../models/llm-tone.dto';
import {LlmTemperatureDTO} from '../../models/llm-temperature.dto';
import {OrganisationDTO} from "../../../../../DTO/Organisation/OrganisationDTO";
import {AuthInfoDTO} from "../../../../../../core/dto/AuthInfoDTO";
import {PermissionService} from "../../../../../../core/authorization/permission.service";
import {LocalizationPipe} from '../../../../../pipe/localization-pipe';
import {LocalizationService} from '../../../../../services/localizationService';

@Component({
  selector: 'app-llm-settings',
  templateUrl: './llm-settings.component.html',
  styleUrls: ['./llm-settings.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    FormsModule,
    LocalizationPipe
  ]
})
export class LlmSettingsComponent implements OnInit {
  @Input() selectedOrganisationId: number;
  @Input() loadOnInit: boolean = true;
  settingsForm: FormGroup;
  organization: OrganisationDTO = new OrganisationDTO();
  created: boolean = false;
  initialSettings: LlmSettingsDTO | null = null;
  activeUser: AuthInfoDTO = new AuthInfoDTO();

  availableModels: LlmModelDTO[] = [
    // GPT Models
    new LlmModelDTO({
      id: 'gpt-4o',
      displayName: 'GPT-4o',
      modelName: 'gpt-4o',
      enabled: true
    }),
    new LlmModelDTO({
      id: 'gpt-4o-mini',
      displayName: 'GPT-4o Mini',
      modelName: 'gpt-4o-mini',
      enabled: true
    }),
    new LlmModelDTO({
      id: 'gpt-4-turbo',
      displayName: 'GPT-4 Turbo',
      modelName: 'gpt-4-turbo',
      enabled: true
    }),
    new LlmModelDTO({
      id: 'gpt-4',
      displayName: 'GPT-4',
      modelName: 'gpt-4',
      enabled: true
    }),
    new LlmModelDTO({
      id: 'gpt-3.5-turbo',
      displayName: 'GPT-3.5 Turbo',
      modelName: 'gpt-3.5-turbo',
      enabled: true
    }),
    new LlmModelDTO({
      id: 'o1-preview',
      displayName: 'OpenAI o1-preview',
      modelName: 'o1-preview',
      enabled: true
    }),
    new LlmModelDTO({
      id: 'o1-mini',
      displayName: 'OpenAI o1-mini',
      modelName: 'o1-mini',
      enabled: true
    }),

    // Gemini Models
    new LlmModelDTO({
      id: 'gemini-2.5-pro',
      displayName: 'Gemini 2.5 Pro',
      modelName: 'gemini-2.5-pro',
      enabled: true
    }),
    new LlmModelDTO({
      id: 'gemini-2.5-flash',
      displayName: 'Gemini 2.5 Flash',
      modelName: 'gemini-2.5-flash',
      enabled: true
    }),
    new LlmModelDTO({
      id: 'gemini-2.0-flash',
      displayName: 'Gemini 2.0 Flash',
      modelName: 'gemini-2.0-flash',
      enabled: true
    }),
    new LlmModelDTO({
      id: 'gemini-1.5-pro',
      displayName: 'Gemini 1.5 Pro',
      modelName: 'gemini-1.5-pro',
      enabled: true
    }),
    new LlmModelDTO({
      id: 'gemini-1.5-flash',
      displayName: 'Gemini 1.5 Flash',
      modelName: 'gemini-1.5-flash',
      enabled: true
    }),
  ];

  availableTones: LlmToneDTO[] = [
    new LlmToneDTO({
      id: 'explanatory',
      name: 'Açıklayıcı',
      description: 'Detaylı ve öğretici bir ton ile açıklamalar yapar'
    }),
    new LlmToneDTO({
      id: 'formal',
      name: 'Resmi',
      description: 'Profesyonel ve ciddi bir iletişim tonu kullanır'
    }),
    new LlmToneDTO({
      id: 'neutral',
      name: 'Nötr',
      description: 'Tarafsız ve dengeli bir ton kullanır'
    }),
    new LlmToneDTO({
      id: 'casual',
      name: 'Samimi',
      description: 'Daha sıcak ve yakın bir ton ile iletişim kurar'
    })
  ];

  availableTemperatures: LlmTemperatureDTO[] = [
    new LlmTemperatureDTO({
      id: 'creative',
      name: 'Yaratıcı',
      value: 0.8,
      description: 'Daha çeşitli ve yaratıcı yanıtlar üretir'
    }),
    new LlmTemperatureDTO({
      id: 'explorer',
      name: 'Kaşif',
      value: 0.6,
      description: 'Yaratıcılık ve tutarlılık arasında denge kurar, keşfedici yanıtlar verir'
    }),
    new LlmTemperatureDTO({
      id: 'balanced',
      name: 'Dengeli',
      value: 0.4,
      description: 'Tutarlı fakat yeterince çeşitlilik içeren yanıtlar sunar'
    }),
    new LlmTemperatureDTO({
      id: 'careful',
      name: 'Dikkatli ',
      value: 0.2,
      description: 'Daha tutarlı, güvenli ve tahmin edilebilir yanıtlar üretir'
    })
  ];

  loading: boolean = false;
  saving: boolean = false;

  // Model için varsayılan eksikliği kontrolü
  get missingModelDefault(): boolean {
    const selectedModels = this.getSelectedModels();
    return selectedModels.length > 0 && !selectedModels.some(m => m.isDefault);
  }

  // Ton için varsayılan eksikliği kontrolü
  get missingToneDefault(): boolean {
    const selectedTones = this.getSelectedTones();
    return selectedTones.length > 0 && !selectedTones.some(t => t.isDefault);
  }

  // Sıcaklık için varsayılan eksikliği kontrolü
  get missingTemperatureDefault(): boolean {
    const selectedTemps = this.getSelectedTemperatures();
    return selectedTemps.length > 0 && !selectedTemps.some(t => t.isDefault);
  }

  // Kayıt için geçerli bir form mu kontrolü
  get isSettingsValid(): boolean {
    // Form geçerli mi?
    if (!this.settingsForm.valid) {
      return false;
    }

    // Varsayılan seçimler uygun mu?
    const selectedModels = this.getSelectedModels();
    const selectedTones = this.getSelectedTones();
    const selectedTemps = this.getSelectedTemperatures();

    // Her seçim grubunda en az bir öğe seçili mi?
    if (selectedModels.length === 0 || selectedTones.length === 0 || selectedTemps.length === 0) {
      return false;
    }

    // Her seçim grubunda bir varsayılan var mı?
    if (!selectedModels.some(m => m.isDefault) ||
        !selectedTones.some(t => t.isDefault) ||
        !selectedTemps.some(t => t.isDefault)) {
      return false;
    }

    return true;
  }

  constructor(
      private fb: FormBuilder,
      private llmSettingsService: LlmSettingsService,
      private snackBar: MatSnackBar,
      private cdr: ChangeDetectorRef,
      public permissionService: PermissionService,
      private localizationService: LocalizationService
  ) {

    this.settingsForm = this.createForm();
  }

  private initializeComponentWithDefaultValues(): void {

    this.settingsForm = this.createForm();
    this.loadSettingsData();
  }

  ngOnInit(): void {
    if (this.loadOnInit || this.loadOnInit == undefined) {
      this.loadSettingsData();
    }
    this.settingsForm.valueChanges.subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  get hasChanges(): boolean {
    if (!this.initialSettings) {
      return this.isFormValid();
    }

    const currentSettings = this.prepareCurrentSettings();

    const patchOperations = compare(this.initialSettings, currentSettings);

    const isEqual = patchOperations.length === 0;

    // Değişiklik var mı?
    return !isEqual;
  }

  private createForm(): FormGroup {
    return this.fb.group({
      welcomeMessageDefault: [''],
      welcomeMessageChatBotUi: ['Merhaba! Ben BilgeBot. Size nasıl yardımcı olabilirim?', [
        Validators.required,
        this.noWhitespaceValidator
      ]],
      welcomeMessageChatWidget: ['Merhaba! Size nasıl yardımcı olabilirim?', [
        Validators.required,
        this.noWhitespaceValidator
      ]],
      unansweredQuestionMessage: ['Üzgünüm, doküman kütüphanesinde bu soruya dair bir bilgi bulamadım. Bu soruya cevap veremediğimi ilgili kişilere bildireceğim. Her gün yeni bilgiler öğreniyorum.', [
        Validators.required,
        this.noWhitespaceValidator
      ]],
      allowGeneralKnowledgeWhenNoCorpus: [false],
      selectedModels: [[]],
      defaultModelId: [''],
      selectedTones: [[]],
      defaultToneId: [''],
      selectedTemperatures: [[]],
      defaultTemperatureId: [''],
      selectOrganization: this.organization
    });
  }

  // Sadece boşluklardan oluşan alanları kontrol eden validator
  noWhitespaceValidator(control: FormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : {'whitespace': true};
  }

  private isFormValid(): boolean {
    return this.settingsForm.valid;
  }

  private prepareCurrentSettings(): LlmSettingsDTO {
    const formValue = this.settingsForm.value;

    // Form değerlerini trim edip kullan
    const trimmedWelcomeMessageDefault = formValue.welcomeMessageDefault ? formValue.welcomeMessageDefault.trim() : '';
    const trimmedWelcomeMessageChatBotUi = formValue.welcomeMessageChatBotUi ? formValue.welcomeMessageChatBotUi.trim() : '';
    const trimmedWelcomeMessageChatWidget = formValue.welcomeMessageChatWidget ? formValue.welcomeMessageChatWidget.trim() : '';
    const trimmedUnansweredQuestionMessage = formValue.unansweredQuestionMessage ? formValue.unansweredQuestionMessage.trim() : '';

    // Seçili modeller - sıralamayı initialSettings'e göre koru
    const availableModels: LlmModelDTO[] = [];
    if (this.initialSettings && this.initialSettings.availableModels) {
      // Önce mevcut modelleri ekle (aynı sırayla)
      this.initialSettings.availableModels.forEach(initialModel => {
        const currentModel = this.availableModels.find(m => 
          m.modelName === initialModel.modelName || m.id === initialModel.id);
        if (currentModel && currentModel.isSelected) {
          const newModel = new LlmModelDTO({
            id: initialModel.id, // Mevcut ID'yi koru
            displayName: currentModel.displayName,
            modelName: currentModel.modelName,
            enabled: currentModel.enabled,
            isDefault: currentModel.isDefault
          });
          availableModels.push(newModel);
        }
      });
      
      // Sonra yeni eklenen modelleri ekle
      this.getSelectedModels().forEach(model => {
        const alreadyExists = availableModels.some(m => 
          m.modelName === model.modelName || m.id === model.id);
        if (!alreadyExists) {
          const newModel = new LlmModelDTO({
            displayName: model.displayName,
            modelName: model.modelName,
            enabled: model.enabled,
            isDefault: model.isDefault
          });
          // Yeni modeller için id property'sini tamamen kaldır
          delete (newModel as any).id;
          availableModels.push(newModel);
        }
      });
    } else {
      // İlk kez oluşturuluyorsa normal yolla ekle
      this.getSelectedModels().forEach(model => {
        const newModel = new LlmModelDTO({
          displayName: model.displayName,
          modelName: model.modelName,
          enabled: model.enabled,
          isDefault: model.isDefault
        });
        delete (newModel as any).id;
        availableModels.push(newModel);
      });
    }

    // Seçili tonlar - sıralamayı initialSettings'e göre koru
    const availableTones: LlmToneDTO[] = [];
    if (this.initialSettings && this.initialSettings.availableTones) {
      // Önce mevcut tonları ekle (aynı sırayla)
      this.initialSettings.availableTones.forEach(initialTone => {
        const currentTone = this.availableTones.find(t => 
          t.name === initialTone.name || t.id === initialTone.id);
        if (currentTone && currentTone.isSelected) {
          const newTone = new LlmToneDTO({
            id: initialTone.id, // Mevcut ID'yi koru
            name: currentTone.name,
            description: currentTone.description,
            isDefault: currentTone.isDefault
          });
          availableTones.push(newTone);
        }
      });
      
      // Sonra yeni eklenen tonları ekle
      this.getSelectedTones().forEach(tone => {
        const alreadyExists = availableTones.some(t => 
          t.name === tone.name || t.id === tone.id);
        if (!alreadyExists) {
          const newTone = new LlmToneDTO({
            name: tone.name,
            description: tone.description,
            isDefault: tone.isDefault
          });
          delete (newTone as any).id;
          availableTones.push(newTone);
        }
      });
    } else {
      // İlk kez oluşturuluyorsa normal yolla ekle
      this.getSelectedTones().forEach(tone => {
        const newTone = new LlmToneDTO({
          name: tone.name,
          description: tone.description,
          isDefault: tone.isDefault
        });
        delete (newTone as any).id;
        availableTones.push(newTone);
      });
    }

    // Seçili sıcaklıklar - sıralamayı initialSettings'e göre koru
    const availableTemperatures: LlmTemperatureDTO[] = [];
    if (this.initialSettings && this.initialSettings.availableTemperatures) {
      // Önce mevcut sıcaklıkları ekle (aynı sırayla)
      this.initialSettings.availableTemperatures.forEach(initialTemp => {
        const currentTemp = this.availableTemperatures.find(t => 
          t.name === initialTemp.name || t.id === initialTemp.id || 
          Math.abs(t.value - initialTemp.value) < 0.01);
        if (currentTemp && currentTemp.isSelected) {
          const newTemp = new LlmTemperatureDTO({
            id: initialTemp.id, // Mevcut ID'yi koru
            name: currentTemp.name,
            value: currentTemp.value,
            description: currentTemp.description,
            isDefault: currentTemp.isDefault
          });
          availableTemperatures.push(newTemp);
        }
      });
      
      // Sonra yeni eklenen sıcaklıkları ekle
      this.getSelectedTemperatures().forEach(temp => {
        const alreadyExists = availableTemperatures.some(t => 
          t.name === temp.name || t.id === temp.id || 
          Math.abs(t.value - temp.value) < 0.01);
        if (!alreadyExists) {
          const newTemp = new LlmTemperatureDTO({
            name: temp.name,
            value: temp.value,
            description: temp.description,
            isDefault: temp.isDefault
          });
          delete (newTemp as any).id;
          availableTemperatures.push(newTemp);
        }
      });
    } else {
      // İlk kez oluşturuluyorsa normal yolla ekle
      this.getSelectedTemperatures().forEach(temp => {
        const newTemp = new LlmTemperatureDTO({
          name: temp.name,
          value: temp.value,
          description: temp.description,
          isDefault: temp.isDefault
        });
        delete (newTemp as any).id;
        availableTemperatures.push(newTemp);
      });
    }

    // Yeni bir ayarlar nesnesi oluştur
    const currentSettings = new LlmSettingsDTO({
      organization: this.organization,

      // Form değerlerini aktar
      welcomeMessageDefault: trimmedWelcomeMessageDefault === '' ? null : trimmedWelcomeMessageDefault,
      welcomeMessageChatBotUi: trimmedWelcomeMessageChatBotUi,
      welcomeMessageChatWidget: trimmedWelcomeMessageChatWidget,
      unansweredQuestionMessage: trimmedUnansweredQuestionMessage,
      allowGeneralKnowledgeWhenNoCorpus: formValue.allowGeneralKnowledgeWhenNoCorpus,

      // Tip güvenli koleksiyonları ata
      availableModels: availableModels,
      availableTones: availableTones,
      availableTemperatures: availableTemperatures
    });

    // ID'yi ayarla (varsa)
    if (this.initialSettings && this.initialSettings.id) {
      currentSettings.id = this.initialSettings.id;
    }

    return currentSettings;
  }

  public loadSettingsData(): void {
    this.loading = true;
    if (this.selectedOrganisationId) {
      this.organization = new OrganisationDTO();
      this.organization.id = this.selectedOrganisationId;
    } else if (window.globalActiveUser && window.globalActiveUser.organisationAccount && window.globalActiveUser.organisationAccount.id) {
      this.organization = new OrganisationDTO();
      this.organization.id = window.globalActiveUser.organisationAccount.id;
    }
    this.initializeDefaultSettings();

    this.llmSettingsService.getOrganizationLlmSettings(this.organization)
        .subscribe({
          next: (response) => {
            if (((response as any).message &&
                Array.isArray((response as any).message) &&
                (response as any).data === null) || response["data"].id == null) {
              this.created = true;
              this.initialSettings = null;
              this.snackBar.open(
                  'Bu kurum için henüz LLM ayarları oluşturulmamış. Varsayılan değerler kullanılıyor.',
                  'Tamam',
                  {duration: 5000}
              );
              this.loading = false;
              return;
            }
            let settingsData = response;
            if ((response as any).data) {
              settingsData = (response as any).data;
            }
            this.created = false;
            this.processSettingsData(settingsData);
            this.updateFormWithSettings(settingsData);
            // Orijinal ayarları sakla
            this.initialSettings = this.prepareCurrentSettings();
            this.loading = false;
            // Değişiklik kontrolü için UI'ı güncelle
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Ayarlar yüklenirken bir hata oluştu:', error);
            this.snackBar.open(
                'Ayarlar yüklenirken bir hata oluştu. Varsayılan değerler gösteriliyor.',
                'Tamam',
                {duration: 3000}
            );
            this.created = true;
            this.initialSettings = null;
            this.loading = false;
          }
        });
  }

  private processSettingsData(settings: LlmSettingsDTO): void {

    if (settings.availableModels && settings.availableModels.length > 0) {

      this.availableModels.forEach(model => {
        model.isSelected = false;
        model.isDefault = false;
      });

      settings.availableModels.forEach(backendModel => {
        const existingModel = this.availableModels.find(m =>
            m.modelName === backendModel.modelName ||
            m.id === backendModel.id
        );

        if (existingModel) {
          existingModel.isSelected = true;
          existingModel.isDefault = backendModel.isDefault;
          existingModel.id = backendModel.id;
        }
      });
    }


    if (settings.availableTones && settings.availableTones.length > 0) {

      this.availableTones.forEach(tone => {
        tone.isSelected = false;
        tone.isDefault = false;
      });

      settings.availableTones.forEach(backendTone => {
        const existingTone = this.availableTones.find(t =>
            t.name === backendTone.name ||
            t.id === backendTone.id
        );

        if (existingTone) {
          existingTone.isSelected = true;
          existingTone.isDefault = backendTone.isDefault;
          existingTone.id = backendTone.id;
        }
      });
    }


    if (settings.availableTemperatures && settings.availableTemperatures.length > 0) {
      this.availableTemperatures.forEach(temp => {
        temp.isSelected = false;
        temp.isDefault = false;
      });

      settings.availableTemperatures.forEach(backendTemp => {
        const existingTemp = this.availableTemperatures.find(t =>
            t.name === backendTemp.name ||
            t.id === backendTemp.id ||
            Math.abs(t.value - backendTemp.value) < 0.01
        );

        if (existingTemp) {
          existingTemp.isSelected = true;
          existingTemp.isDefault = backendTemp.isDefault;
          existingTemp.id = backendTemp.id;
        }
      });
    }

  }

  private initializeDefaultSettings(): void {
    const defaultModel = this.availableModels.find(m => m.displayName.includes('GPT-4o'));
    if (defaultModel) {
      defaultModel.isSelected = true;
      defaultModel.isDefault = true;
      this.settingsForm.get('defaultModelId')?.setValue(defaultModel.id);
    }

    const defaultTone = this.availableTones.find(t => t.name.includes('Nötr'));
    if (defaultTone) {
      defaultTone.isSelected = true;
      defaultTone.isDefault = true;
      this.settingsForm.get('defaultToneId')?.setValue(defaultTone.id);
    }

    const defaultTemp = this.availableTemperatures.find(t => t.name.includes('Dengeli'));
    if (defaultTemp) {
      defaultTemp.isSelected = true;
      defaultTemp.isDefault = true;
      this.settingsForm.get('defaultTemperatureId')?.setValue(defaultTemp.id);
    }

    this.settingsForm.get('selectedModels')?.setValue(this.availableModels.filter(m => m.isSelected));
    this.settingsForm.get('selectedTones')?.setValue(this.availableTones.filter(t => t.isSelected));
    this.settingsForm.get('selectedTemperatures')?.setValue(this.availableTemperatures.filter(t => t.isSelected));
  }

  private updateFormWithSettings(settings: LlmSettingsDTO): void {
    this.settingsForm.patchValue({
      welcomeMessageDefault: settings.welcomeMessageDefault || '',
      welcomeMessageChatBotUi: settings.welcomeMessageChatBotUi || '',
      welcomeMessageChatWidget: settings.welcomeMessageChatWidget || '',
      unansweredQuestionMessage: settings.unansweredQuestionMessage || '',
      allowGeneralKnowledgeWhenNoCorpus: settings.allowGeneralKnowledgeWhenNoCorpus || false,
      selectedModels: this.availableModels.filter(m => m.isSelected),
      selectedTones: this.availableTones.filter(t => t.isSelected),
      selectedTemperatures: this.availableTemperatures.filter(t => t.isSelected)
    });

    this.setDefaultValuesFromSettings();
  }

  private setDefaultValuesFromSettings(): void {
    const defaultModel = this.availableModels.find(m => m.isDefault === true);
    if (defaultModel) {
      defaultModel.isSelected = true;
      this.settingsForm.get('defaultModelId')?.setValue(defaultModel.id);
    } else {
      this.settingsForm.get('defaultModelId')?.setValue('');
    }

    const defaultTone = this.availableTones.find(t => t.isDefault === true);
    if (defaultTone) {
      defaultTone.isSelected = true;
      this.settingsForm.get('defaultToneId')?.setValue(defaultTone.id);
    } else {
      this.settingsForm.get('defaultToneId')?.setValue('');
    }

    const defaultTemp = this.availableTemperatures.find(t => t.isDefault === true);
    if (defaultTemp) {
      defaultTemp.isSelected = true;
      this.settingsForm.get('defaultTemperatureId')?.setValue(defaultTemp.id);
    } else {
      this.settingsForm.get('defaultTemperatureId')?.setValue('');
    }
  }

  onModelSelectionChange(model: LlmModelDTO, isChecked: boolean): void {
    model.isSelected = isChecked;

    this.settingsForm.get('selectedModels')?.setValue(
        this.availableModels.filter(m => m.isSelected)
    );

    // Model seçimi kaldırılırsa ve varsayılan ise, başka bir modeli varsayılan yap
    if (!isChecked && model.isDefault) {
      model.isDefault = false;
      const otherSelectedModels = this.availableModels.filter(m => m.isSelected);
      if (otherSelectedModels.length > 0) {
        this.updateDefaultModelOnly(otherSelectedModels[0].id);
      } else {
        this.settingsForm.get('defaultModelId')?.setValue('');
      }
    }

    // Değişiklik kontrolü ve UI güncellemesi
    this.settingsForm.markAsDirty();
    this.cdr.detectChanges();
  }

  onToneSelectionChange(tone: LlmToneDTO, isChecked: boolean): void {
    tone.isSelected = isChecked;

    const selectedTones = this.availableTones.filter(t => t.isSelected);
    this.settingsForm.get('selectedTones')?.setValue(selectedTones);

    if (!isChecked && tone.isDefault) {
      tone.isDefault = false;
      if (selectedTones.length > 0) {
        this.updateDefaultTone(selectedTones[0].id);
      } else {
        this.settingsForm.get('defaultToneId')?.setValue('');
      }
    } else if (selectedTones.length === 1 && !selectedTones.some(t => t.isDefault)) {
      // Eğer tek seçenek varsa ve varsayılan yoksa, onu varsayılan yap
      this.updateDefaultTone(selectedTones[0].id);
    }

    // Değişiklik kontrolü ve UI güncellemesi
    this.settingsForm.markAsDirty();
    this.cdr.detectChanges();
  }

  onTemperatureSelectionChange(temp: LlmTemperatureDTO, isChecked: boolean): void {
    temp.isSelected = isChecked;

    const selectedTemps = this.availableTemperatures.filter(t => t.isSelected);
    this.settingsForm.get('selectedTemperatures')?.setValue(selectedTemps);

    if (!isChecked && temp.isDefault) {
      temp.isDefault = false;
      if (selectedTemps.length > 0) {
        this.updateDefaultTemperature(selectedTemps[0].id);
      } else {
        this.settingsForm.get('defaultTemperatureId')?.setValue('');
      }
    } else if (selectedTemps.length === 1 && !selectedTemps.some(t => t.isDefault)) {
      // Eğer tek seçenek varsa ve varsayılan yoksa, onu varsayılan yap
      this.updateDefaultTemperature(selectedTemps[0].id);
    }

    // Değişiklik kontrolü ve UI güncellemesi
    this.settingsForm.markAsDirty();
    this.cdr.detectChanges();
  }

  saveSettings(): void {
    // Seçilen model, ton ve sıcaklık var mı kontrol edelim
    const selectedModels = this.getSelectedModels();
    const selectedTones = this.getSelectedTones();
    const selectedTemperatures = this.getSelectedTemperatures();

    // Seçim yapılmamış kategorileri belirleyelim
    const emptySelections = [];
    if (selectedModels.length === 0) emptySelections.push(this.localizationService.getLocalizationValue('LlmSettings.Model'));
    if (selectedTones.length === 0) emptySelections.push(this.localizationService.getLocalizationValue('LlmSettings.Tone'));
    if (selectedTemperatures.length === 0) emptySelections.push(this.localizationService.getLocalizationValue('LlmSettings.CreativityMode'));

    // Boş seçim varsa uyarı gösterelim
    if (emptySelections.length > 0) {
      const errorMessage = this.localizationService.getLocalizationValue('LlmSettings.PleaseSelectAtLeastOne') + ' ' + emptySelections.join(', ');
      this.snackBar.open(errorMessage, this.localizationService.getLocalizationValue('Common.Ok'), {duration: 3000});
      return;
    }

    // Varsayılan değerler seçilmiş mi kontrol edelim
    const missingDefaults = [];
    if (selectedModels.length > 0 && !selectedModels.some(m => m.isDefault)) missingDefaults.push(this.localizationService.getLocalizationValue('LlmSettings.Model'));
    if (selectedTones.length > 0 && !selectedTones.some(t => t.isDefault)) missingDefaults.push(this.localizationService.getLocalizationValue('LlmSettings.Tone'));
    if (selectedTemperatures.length > 0 && !selectedTemperatures.some(t => t.isDefault)) missingDefaults.push(this.localizationService.getLocalizationValue('LlmSettings.CreativityMode'));

    // Eksik varsayılan varsa uyarı gösterelim
    if (missingDefaults.length > 0) {
      const errorMessage = this.localizationService.getLocalizationValue('LlmSettings.PleaseSelectDefault') + ' ' + missingDefaults.join(', ');
      this.snackBar.open(errorMessage, this.localizationService.getLocalizationValue('Common.Ok'), {duration: 3000});
      return;
    }

    // Her kategori için tek bir varsayılan olduğundan emin olalım
    this.ensureOnlyOneDefault(this.availableModels);
    this.ensureOnlyOneDefault(this.availableTones);
    this.ensureOnlyOneDefault(this.availableTemperatures);

    if (this.settingsForm.invalid) {
      this.markFormGroupTouched(this.settingsForm);
      this.snackBar.open(this.localizationService.getLocalizationValue('LlmSettings.PleaseAllRequiredFields'), this.localizationService.getLocalizationValue('Common.Ok'), {duration: 3000});
      return;
    }

    this.loading = true;

    this.llmSettingsService.getOrganizationLlmSettings(this.organization)
        .subscribe({
          next: (response) => {
            let existingSettings = response;

            if ((response as any).data) {
              existingSettings = (response as any).data;
            }

            if (!existingSettings || !existingSettings.id) {
              this.createNewSettings();
              this.loading = false;
              return;
            }

            // Güncel ayarları hazırla
            const currentSettings = this.prepareCurrentSettings();

            // JsonPatch ile değişiklikleri otomatik olarak algıla
            const patchOperations = compare(this.initialSettings, currentSettings);

            this.saving = true;
            this.loading = false;

            if (patchOperations.length === 0) {
              this.snackBar.open(this.localizationService.getLocalizationValue('LlmSettings.NoChanges'), this.localizationService.getLocalizationValue('Common.Ok'), {duration: 3000});
              this.saving = false;
              return;
            }

            this.llmSettingsService.patchOrganizationLlmSettings(
                existingSettings.id,
                patchOperations
            ).subscribe({
              next: (response) => {
                this.snackBar.open(this.localizationService.getLocalizationValue('LlmSettings.SettingsSavedSuccess'), this.localizationService.getLocalizationValue('Common.Ok'), {duration: 3000});
                this.initialSettings = currentSettings;
                this.saving = false;
                this.cdr.detectChanges();
              },
              error: (error) => {
                console.error('Ayarlar güncellenirken hata oluştu:', error);
                this.snackBar.open(this.localizationService.getLocalizationValue('LlmSettings.ErrorUpdatingSettings'), this.localizationService.getLocalizationValue('Common.Ok'), {duration: 3000});
                this.saving = false;
              }
            });
          },
          error: (error) => {
            this.createNewSettings();
            this.loading = false;
          }
        });
  }

  private createNewSettings(): void {
    // Güncel ayarları hazırla
    const currentSettings = this.prepareCurrentSettings();

    this.saving = true;
    this.llmSettingsService.createOrganizationLlmSettings(currentSettings)
        .subscribe({
          next: (response) => {
            this.snackBar.open(this.localizationService.getLocalizationValue('LlmSettings.SettingsCreatedSuccess'), this.localizationService.getLocalizationValue('Common.Ok'), {duration: 3000});
            // Yeni ayarları başlangıç noktası olarak kaydet
            this.initialSettings = currentSettings;
            this.saving = false;
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Ayarlar oluşturulurken hata oluştu:', error);
            this.snackBar.open(this.localizationService.getLocalizationValue('LlmSettings.ErrorCreatingSettings'), this.localizationService.getLocalizationValue('Common.Ok'), {duration: 3000});
            this.saving = false;
          }
        });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getSelectedModels(): LlmModelDTO[] {
    return this.availableModels.filter(model => model.isSelected);
  }

  getSelectedTones(): LlmToneDTO[] {
    return this.availableTones.filter(tone => tone.isSelected);
  }

  getSelectedTemperatures(): LlmTemperatureDTO[] {
    return this.availableTemperatures.filter(temp => temp.isSelected);
  }

  hasSelectedItems(collection: any[]): boolean {
    return collection.some(item => item.isSelected);
  }

  private validateDefaultSelection(collection: any[]): boolean {
    const selectedItems = collection.filter(item => item.isSelected);

    if (selectedItems.length === 0) {
      return false; // Hiç seçim yoksa geçersiz
    }

    if (selectedItems.length === 1) {
      // Tek seçim varsa, onu varsayılan yap
      collection.forEach(item => item.isDefault = false);
      selectedItems[0].isDefault = true;
      return true;
    }

    // Birden fazla seçim var, en az bir varsayılan olmalı
    const hasDefault = selectedItems.some(item => item.isDefault === true);

    if (!hasDefault) {
      // Varsayılan yoksa ilkini seç
      collection.forEach(item => item.isDefault = false);
      selectedItems[0].isDefault = true;
    }

    return true;
  }

  updateDefaultModelOnly(modelId: string): void {
    // Önce tüm modellerdeki varsayılan işaretini kaldır
    this.availableModels.forEach(model => model.isDefault = false);

    // Seçilen modeli varsayılan olarak işaretle
    const selectedModel = this.availableModels.find(model => model.id === modelId);
    if (selectedModel) {
      selectedModel.isDefault = true;
      selectedModel.isSelected = true; // Varsayılan olarak işaretlenen model seçili olmalı
      this.settingsForm.get('defaultModelId')?.setValue(modelId);
    }

    // Form değişikliğini işaretle
    this.settingsForm.markAsDirty();

    // Değişiklikler için UI'ı güncelle
    this.cdr.detectChanges();
  }

  updateDefaultTone(toneId: string): void {
    // Önce tüm tonlardaki varsayılan işaretini kaldır
    this.availableTones.forEach(tone => tone.isDefault = false);

    // Seçilen tonu varsayılan olarak işaretle
    const selectedTone = this.availableTones.find(tone => tone.id === toneId);
    if (selectedTone) {
      selectedTone.isDefault = true;
      selectedTone.isSelected = true; // Varsayılan olarak işaretlenen ton seçili olmalı
      this.settingsForm.get('defaultToneId')?.setValue(toneId);
    }

    // Form değişikliğini işaretle
    this.settingsForm.markAsDirty();

    // Değişiklikler için UI'ı güncelle
    this.cdr.detectChanges();
  }

  updateDefaultTemperature(tempId: string): void {
    // Önce tüm sıcaklıklardaki varsayılan işaretini kaldır
    this.availableTemperatures.forEach(temp => temp.isDefault = false);

    // Seçilen sıcaklığı varsayılan olarak işaretle
    const selectedTemp = this.availableTemperatures.find(temp => temp.id === tempId);
    if (selectedTemp) {
      selectedTemp.isDefault = true;
      selectedTemp.isSelected = true; // Varsayılan olarak işaretlenen sıcaklık seçili olmalı
      this.settingsForm.get('defaultTemperatureId')?.setValue(tempId);
    }

    // Form değişikliğini işaretle
    this.settingsForm.markAsDirty();

    // Değişiklikler için UI'ı güncelle
    this.cdr.detectChanges();
  }

  private ensureOnlyOneDefault(collection: any[]): void {
    const selectedItems = collection.filter(item => item.isSelected);

    if (selectedItems.length === 0) {
      return;
    }

    // Varsayılan olarak işaretlenmiş öğeleri bul
    const defaultItems = selectedItems.filter(item => item.isDefault === true);

    // Birden fazla varsayılan varsa, sadece ilkini bırak
    if (defaultItems.length > 1) {
      // İlki hariç diğerlerinin varsayılan işaretini kaldır
      for (let i = 1; i < defaultItems.length; i++) {
        defaultItems[i].isDefault = false;
      }
    }
    // Hiç varsayılan yoksa, ilk seçili öğeyi varsayılan yap
    else if (defaultItems.length === 0 && selectedItems.length > 0) {
      selectedItems[0].isDefault = true;
    }
  }

  onCancel(): void {
    if (this.created) {
      this.settingsForm.reset();
      this.initializeDefaultSettings();
      this.settingsForm.markAsPristine(); // Form değişiklik durumunu temizle
      this.cdr.detectChanges();
    } else {
      this.loadSettingsData();
      this.settingsForm.markAsPristine(); // Form değişiklik durumunu temizle
    }
  }
}
